import Link from "next/link";
import { safeHref } from "../../lib/safeLinks";

// Internal paths render as client-side links; web links open in a new tab; anything unsafe
// (javascript:, data:, ...) or missing renders nothing.
const CtaLink = ({ href: rawHref, className, style, children }) => {
  const href = safeHref(rawHref);
  if (!href) return null;
  return href.startsWith("/") ? (
    <Link href={href} className={className} style={style}>
      {children}
    </Link>
  ) : (
    <a href={href} className={className} style={style} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const AppCard = ({ app }) => {
  const accent = app.accentColor || "#8358ff";
  return (
    <article className="dark:bg-jacarta-700 dark:border-jacarta-600 border-jacarta-100 rounded-2.5xl flex flex-col border bg-white p-8 transition-shadow hover:shadow-lg">
      <div className="mb-6 flex items-center space-x-4">
        {app.icon?.url && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={`${app.icon.url}?w=128&h=128&fm=png`}
            alt={app.icon.description || `${app.name} icon`}
            width={64}
            height={64}
            className="h-16 w-16 rounded-2xl"
          />
        )}
        <div>
          <h3 className="font-display text-jacarta-700 text-2xl font-semibold dark:text-white">{app.name}</h3>
          <span
            className="mt-1 inline-block rounded-full px-3 py-0.5 text-xs font-semibold text-white"
            style={{ backgroundColor: accent }}
          >
            {app.status}
          </span>
        </div>
      </div>

      <p className="text-jacarta-700 mb-1 text-lg font-semibold dark:text-white">{app.tagline}</p>
      {app.motto && <p className="text-jacarta-500 dark:text-jacarta-300 mb-4 italic">{app.motto}</p>}
      <p className="dark:text-jacarta-200 mb-6 mt-3 leading-relaxed">{app.story}</p>

      {app.features?.length > 0 && (
        <ul className="dark:text-jacarta-200 mb-8 space-y-2 text-sm">
          {app.features.map((feature) => (
            <li key={feature} className="flex items-start">
              <span className="mt-1.5 mr-3 inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto">
        {app.statusNote && <p className="text-jacarta-500 dark:text-jacarta-300 mb-4 text-sm">{app.statusNote}</p>}
        <div className="flex flex-wrap items-center gap-4">
          {app.ctaLabel && (
          <CtaLink
            href={app.ctaUrl}
            className="rounded-full py-3 px-8 text-center font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            {app.ctaLabel}
          </CtaLink>
          )}
          {app.websiteUrl && app.websiteUrl !== app.ctaUrl && (
            <CtaLink href={app.websiteUrl} className="text-jacarta-500 hover:text-accent dark:text-jacarta-300 text-sm font-semibold">
              Visit {app.name} &rarr;
            </CtaLink>
          )}
        </div>
      </div>
    </article>
  );
};

const AppShowcase = ({ apps }) => {
  if (!apps?.length) return null;
  return (
    <section id="apps" className="dark:bg-jacarta-800 relative scroll-mt-24 py-24">
      <div className="container">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <h2 className="font-display text-jacarta-700 mb-4 text-3xl dark:text-white">Our apps</h2>
          <p className="dark:text-jacarta-300 text-lg">
            Each one started as something our own family needed.
          </p>
        </div>
        <div className="grid gap-8 lg:grid-cols-3">
          {apps.map((app) => (
            <AppCard key={app.slug} app={app} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
